<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

const props = defineProps({
  conversationId: { type: Number, default: null },
  hasContent: { type: Boolean, default: false },
  editorContent: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  isEditorDisabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:open', 'executeCopilotAction', 'closeMenu']);

const open = defineModel('open', { type: Boolean, default: false });
const { t } = useI18n();
const { captainTasksEnabled } = useCaptain();
const triggerRef = ref(null);
const menuRef = ref(null);

const isBlocked = computed(
  () => props.disabled || props.isEditorDisabled || !captainTasksEnabled.value
);

const openAssist = () => {
  if (isBlocked.value) return;
  if (!open.value) {
    useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
      conversationId: props.conversationId,
      entryPoint: 'more_menu',
    });
  }
  open.value = true;
};

const closeAssist = (restoreFocus = false) => {
  open.value = false;
  if (restoreFocus) nextTick(() => triggerRef.value?.$el?.focus());
};

const onAction = (action, data) => {
  if (isBlocked.value) return;
  emit('executeCopilotAction', action, data || props.editorContent);
  open.value = false;
  emit('closeMenu');
};

const onCopilotClose = () => {
  open.value = false;
  emit('closeMenu');
};

const closeWithFocus = () => closeAssist(true);

watch(
  () => props.conversationId,
  () => {
    open.value = false;
  }
);

watch(open, async isOpen => {
  if (isOpen) {
    await nextTick();
    menuRef.value?.querySelector('button')?.focus();
  }
});
watch(isBlocked, blocked => {
  if (blocked) closeAssist();
});

defineExpose({ closeWithFocus });
</script>

<template>
  <div
    data-composer-nested-menu
    data-testid="composer-write-assist-wrap"
    @click.stop
  >
    <div
      v-if="open && !isBlocked"
      ref="menuRef"
      data-testid="composer-write-assist-menu"
    >
      <CopilotMenuBar
        embedded
        :has-selection="false"
        :has-content="hasContent"
        :conversation-id="conversationId"
        @execute-copilot-action="onAction"
        @close="onCopilotClose"
        @back="closeAssist(true)"
      />
    </div>
    <NextButton
      v-else-if="captainTasksEnabled"
      ref="triggerRef"
      data-testid="composer-write-assist"
      :label="t('CONVERSATION.REPLYBOX.HELP_WRITE')"
      :aria-label="t('CONVERSATION.REPLYBOX.HELP_WRITE')"
      :aria-expanded="open"
      :disabled="isBlocked"
      color="slate"
      variant="ghost"
      class="min-h-11 min-w-11 w-full !justify-start"
      @click="openAssist"
    >
      <template #icon>
        <BrasaMascot :size="20" :animated="false" aria-hidden="true" />
      </template>
    </NextButton>
  </div>
</template>
